import { getAnalytics } from "@tm/insurance/analytics";
import { trackEvent, updateSearchContext } from "@tm/insurance/analytics/xdm";
import {
  debounce,
  replaceHistory,
  replaceHistoryRemoveParams,
} from "@tm/insurance/utils";
import { LandingButton, ParaTag, TMFloatingInput } from "@tm/static";
import { usePlanType } from "@tm/ui";
import { theme } from "@tm/utility/global";
import Fuse from "fuse.js";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import RegionCountryModal from "../RegionModalDweb/RegionCountryModal";
import WebToast from "../Toast/WebToast";
import CountryList from "./CountryList";
import {
  CountryInput,
  DropdownContainer,
  DropdownContent,
  InputIcon,
  InputWrapper,
  ScrollableContent,
  StickyButtonContainer,
  StickySearchContainer,
} from "./SelectCountryDropdown.style";
import { BtnWrap, CountrySectionWrap } from "./SelectCountryModalV2.style";

const analytics = getAnalytics();

const DEBOUNCE_TIME = 300;

const fuzzySearch = (
  searcher: Fuse<any>,
  searchValue: string,
  displayCountryData: any[],
  countryData: any[],
  setDisplayCountrysData: React.Dispatch<React.SetStateAction<any[]>>
) => {
  const selectedCountriesData = displayCountryData.filter(
    (country: any) => country.isSelected && !country.isDisabled
  );
  const searchResults = searcher.search(searchValue);
  const filteredData = searchResults?.map(
    (match: { item: any }) => match?.item
  );

  const originalSortData = countryData.map((country: any) => {
    return displayCountryData.find((item: any) => item.code === country.code);
  });

  const allObjects = [
    ...selectedCountriesData,
    ...filteredData,
    ...originalSortData,
  ];
  const tempObj = allObjects.reduce((acc: any, obj: any) => {
    if (obj && obj.code && !acc.hasOwnProperty(obj.code)) {
      acc[obj.code] = obj;
    }
    return acc;
  }, {});
  const uniqueObjects = Object.values(tempObj);
  setDisplayCountrysData(uniqueObjects);
};
const debouncedSearch = debounce(fuzzySearch, DEBOUNCE_TIME);

interface SelectCountryModalProps {
  handleSelectCountry: () => void;
  countryData: {
    code?: string;
    countryCount?: string;
    descTitle?: string;
    description?: string;
    isPopular?: boolean;
    keywords?: string;
    img?: string;
    subTitle?: string;
    isMandatory?: string;
    regionNames?: string;
    regionCodes?: string[];
    name?: string;
    type?: string;
  }[];
  countrySelectErrMsg?: string;
  popularRegion?: {
    id: number;
    regionName?: string;
    regionalCountryTxt?: string;
    countryList?: string[];
    isSelected?: boolean;
    regionInfo?: string;
  }[];
  showCountry: {
    code?: string;
    countryCount?: string;
    descTitle?: string;
    description?: string;
    isPopular?: boolean;
    keywords?: string;
    img?: string;
    subTitle?: string;
    isMandatory?: string;
    regionNames?: string;
    regionCodes?: string[];
    name?: string;
    type?: string;
  }[];
  customSubTitle?: string;
  setShowCountry: Dispatch<SetStateAction<any[] | undefined>>;
  setIsCountryError: Dispatch<SetStateAction<boolean>>;
  popularCountries?: {
    code: string;
    isMandatory: boolean;
    isPopular: boolean;
    name: string;
    keywords: string;
    flag: string;
    img: string;
  }[];
  handleDescriptionModal: (coiuntry: any) => string;
  setCountryData: Dispatch<SetStateAction<any[]>>;
  setPopularCountries: Dispatch<SetStateAction<any[]>>;
  focusSearchOnOpen?: boolean;
  isDropdown?: boolean;
  travelNameRef?: React.RefObject<HTMLDivElement>;
}

const SelectCountryModalV2 = ({
  handleSelectCountry,
  countryData,
  setShowCountry,
  showCountry,
  setCountryData,
  countrySelectErrMsg,
  setIsCountryError,
  popularCountries,
  setPopularCountries,
  customSubTitle,
  handleDescriptionModal,
  focusSearchOnOpen = false,
  isDropdown = false,
  travelNameRef,
}: SelectCountryModalProps) => {
  const [displayCountryData, setDisplayCountrysData] =
    useState<any[]>(countryData);
  const [searchTxt, setSearchTxt] = useState("");
  const [isSelectedError, setIsSelectedError] = useState<boolean>(false);
  const [countrySelectToastMsg, setCountrySelectToastMsg] =
    useState<string>("");
  const searchedCountriesOmn = useRef<string[]>([]);
  const selectedCountriesOmn = useRef<string[]>([]);
  const singleSearchSelection = useRef<boolean>(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const { type } = usePlanType();

  const searcher = new Fuse(displayCountryData, {
    keys: [
      "name",
      "keywords",
      "regionName",
      "description",
      "code",
      "regionCodes",
    ],
    isCaseSensitive: false,
    shouldSort: true,
    findAllMatches: true,
    threshold: 0.5,
    minMatchCharLength: 1,
  });

  const noSearchResults = (addTimeOut: boolean) => {
    let allObjects = [];
    const selectedHeader = {
      type: "HEADER",
      name: "Selected Countries",
      code: "SELECTED_COUNTRIES",
    };
    const selectedCountriesData = displayCountryData.filter((country) => {
      return country.isSelected && !country.isDisabled;
    });
    const originalSortData = countryData.map((country) => {
      return displayCountryData.find((item) => item.code === country.code);
    });
    if (selectedCountriesData.length) {
      allObjects = [
        { ...selectedHeader },
        ...selectedCountriesData,
        ...originalSortData,
      ];
      const tempObj = allObjects.reduce((acc, obj) => {
        if (obj && obj.code && !acc.hasOwnProperty(obj.code)) {
          acc[obj.code] = obj;
        }
        return acc;
      }, {});
      const uniqueObjects = Object.values(tempObj);
      addTimeOut
        ? setTimeout(() => {
            setDisplayCountrysData(uniqueObjects);
          }, DEBOUNCE_TIME)
        : setDisplayCountrysData(uniqueObjects);
    } else {
      addTimeOut
        ? setTimeout(() => {
            setDisplayCountrysData(originalSortData);
          }, DEBOUNCE_TIME)
        : setDisplayCountrysData(originalSortData);
    }
  };
  useEffect(() => {
    noSearchResults(false);
    analytics?.trackLandingEvents(
      {
        actionType: `Ins_Country_popuploaded`,
      },
      "pageView",
      type
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "popup-rendered",
        event_value: `Ins_Country_popuploaded`,
      },
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isDropdown &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        travelNameRef?.current &&
        !travelNameRef?.current?.contains(event.target as Node)
      ) {
        onCountrySubmit();
        event.stopPropagation();
      }
    };

    if (isDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    isDropdown,
    travelNameRef,
    displayCountryData,
    countryData,
    popularCountries,
  ]);

  const countryClickHandler = (code: string) => {
    handleDescriptionModal(
      displayCountryData.filter((data: any) => data.code === code)[0]
    );
    let selected = "";
    let updatedCountryArr = displayCountryData?.map((country: any) => {
      if (country.code === code) {
        if (country["isSelected"]) {
          country["isSelected"] = false;
          return { ...country };
        }
        /* istanbul ignore next */
        if (selectedCountriesOmn?.current?.length >= 30) {
          selectedCountriesOmn.current = [
            ...selectedCountriesOmn?.current?.slice(1),
            country.code,
          ];
        } else {
          selectedCountriesOmn.current = [
            ...selectedCountriesOmn.current,
            country.code,
          ];
        }
        analytics?.trackLandingEvents(
          {
            actionType: `Ins_Country_popup_checkbox:${country.name}`,
          },
          "pageView",
          type
        );
        selected = country?.name || "";
        return { ...country, isSelected: true, isDisabled: false };
      }
      return { ...country, isDisabled: false };
    });

    const isExclusiveCountrySelected = updatedCountryArr.find(
      (item: any) => item.isExclusiveSelection && item.isSelected
    );

    updatedCountryArr = updatedCountryArr.map((country: any) => {
      if (isExclusiveCountrySelected) {
        return country.isExclusiveSelection
          ? { ...country }
          : { ...country, isSelected: false, isDisabled: true };
      } else if (updatedCountryArr.some((item: any) => item.isSelected)) {
        return country.isExclusiveSelection
          ? { ...country, isDisabled: true }
          : { ...country };
      }
      return { ...country };
    });

    const selectedCountryCodes = updatedCountryArr
      .filter((country: any) => country.isSelected && country.type === "REGION")
      .map((country: any) => {
        return country.code;
      });
    selectedCountryCodes.map((item: any) => {
      updatedCountryArr = updatedCountryArr.map((country: any) => {
        if (country.regionCodes && country.regionCodes.includes(item)) {
          return { ...country, isDisabled: true };
        }
        return { ...country };
      });
    });

    const selectedCountries = updatedCountryArr.filter(
      (country: any) => country.isSelected && !country.isDisabled
    );

    if (selected) {
      trackEvent(
        {
          event_detail: {
            event_type: "action",
            event_name: "component_cta-clicked",
            event_value: `Ins_Country_popup_checkbox:${selected}`,
          },
        },
        {
          countries: selectedCountries,
        }
      );
    } else {
      updateSearchContext({
        countries: selectedCountries,
      });
    }
    setDisplayCountrysData(updatedCountryArr);
    const popularCountryData = updatedCountryArr.filter(
      (country: any) => country.isSelected || country.isPopular
    );
    setPopularCountries([
      ...popularCountryData.filter(
        (country: any) => country.isSelected && !country.isDisabled
      ),
      ...popularCountryData.filter((country: any) => country.isDisabled),
      ...popularCountryData.filter(
        (country: any) => !country.isSelected && !country.isDisabled
      ),
    ]);
  };

  const handleSearchtxt = useCallback(
    (searchValue: string) => {
      debouncedSearch(
        searcher,
        searchValue,
        displayCountryData,
        countryData,
        setDisplayCountrysData
      );
    },
    [displayCountryData]
  );

  const onSearchChange = (searchValue: string) => {
    if (searchValue) {
      handleSearchtxt(searchValue);
    } else {
      noSearchResults(true);
    }
  };

  const searchQueryHighlighter = (ele: string) => {
    const withSpecialCharsSupport = searchTxt?.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&"
    );

    const searchRegex = new RegExp(`(${withSpecialCharsSupport})`, "ig");

    const highlighted = ele.replace(
      searchRegex,
      (match) => `<span class="highlight">${match}</span>`
    );

    return <ParaTag dangerousText={highlighted} />;
  };

  const isCheckedHandler = useCallback(
    (data: any) => {
      return data.isSelected;
    },
    [displayCountryData, searchTxt]
  );

  const handleCrossClick = () => {
    setPopularCountries(showCountry);
    if (searchedCountriesOmn?.current?.length) {
      analytics?.trackLandingEvents(
        {
          actionType: "searched_countries",
          payload: searchedCountriesOmn?.current?.join("|") + "|",
        },
        "pageView",
        type
      );
      searchedCountriesOmn.current = [];
    }
    let tempCountryData: any = displayCountryData.map((country: any) => {
      return { ...country, isSelected: false, isDisabled: false };
    });
    showCountry.map((data: any) => {
      tempCountryData = tempCountryData.map((country: any) => {
        if (data.code === country.code) {
          return {
            ...country,
            isSelected: data.isSelected,
            isDisabled: data.isDisabled,
          };
        }
        return { ...country };
      });
    });

    setDisplayCountrysData({ ...tempCountryData });
    handleSelectCountry();
  };

  const onCountrySubmit = () => {
    analytics?.trackLandingEvents(
      {
        actionType: "Ins_Country_popup_AddDestinationClicked",
      },
      "pageView",
      type
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "Ins_Country_popup_AddDestinationClicked",
      },
    });
    setIsCountryError(false);
    if (searchedCountriesOmn?.current?.length) {
      analytics?.trackLandingEvents(
        {
          actionType: "searched_countries",
          payload: searchedCountriesOmn?.current?.join("|") + "|",
        },
        "pageView",
        type
      );
      searchedCountriesOmn.current = [];
    }
    if (selectedCountriesOmn?.current?.length) {
      analytics?.trackLandingEvents(
        {
          actionType: "selected_countries",
          payload: selectedCountriesOmn?.current?.join("|"),
        },
        "pageView",
        type
      );
      selectedCountriesOmn.current = [];
    }
    let countryDataArr = countryData.map((country: any) => {
      return { ...country, isSelected: false, isDisabled: false };
    });

    displayCountryData.map((data: any) => {
      countryDataArr = countryDataArr.map((country: any) => {
        if (country.code === data.code) {
          return {
            ...country,
            isSelected: data.isSelected,
            isDisabled: data.isDisabled,
          };
        }
        return { ...country };
      });
    });
    setCountryData(countryDataArr);
    setShowCountry(popularCountries);

    const selectedCountryCodes = displayCountryData
      .filter((item: any) => item.isSelected && !item.isDisabled)
      .map((item: any) => item.code);

    replaceHistoryRemoveParams("countries");
    replaceHistory("countries", selectedCountryCodes.join(","));
    const selectedNonPopCountries = displayCountryData.filter(
      (data: any) => data.isSelected && !data.isPopular
    );
    analytics?.trackLandingEvents(
      {
        actionType: "other_countries_clicked",
        payload: selectedNonPopCountries.map((c: any) => c.name).join("|"),
      },
      "pageView",
      type
    );

    handleSelectCountry && handleSelectCountry();
  };

  const handleSearchTxt = (event: any) => {
    if (event.nativeEvent.inputType === "deleteContentBackward") {
      if (searchedCountriesOmn?.current?.length >= 30) {
        searchedCountriesOmn.current = [
          ...searchedCountriesOmn?.current?.slice(1),
          searchTxt,
        ];
      } else {
        searchedCountriesOmn.current = [
          ...searchedCountriesOmn?.current,
          searchTxt,
        ];
      }
    }
    setSearchTxt((_) => event.target.value);
    onSearchChange(event.target.value);
  };

  const searchClickAnalytics = () => {
    analytics?.trackLandingEvents(
      {
        actionType: "Ins_Country_popup_searchclicked",
      },
      "pageView",
      type
    );
    if (!singleSearchSelection.current) {
      singleSearchSelection.current = true;
      trackEvent({
        event_detail: {
          event_type: "action",
          event_name: "component_cta-clicked",
          event_value: "Ins_Country_popup_searchclicked",
        },
      });
    }
  };

  const renderContent = () => (
    <>
      <div onClick={searchClickAnalytics}>
        <TMFloatingInput
          handleChange={(e) => handleSearchTxt(e)}
          inputValue={searchTxt}
          labelTxt="Search Country/Region"
          defaultFocus={focusSearchOnOpen}
        />
      </div>
      <CountrySectionWrap>
        {displayCountryData?.length ? (
          <CountryList
            countryData={
              searchTxt.length
                ? displayCountryData.filter(
                    (country) => country.type !== "HEADER"
                  )
                : displayCountryData
            }
            countryHandler={countryClickHandler}
            isCheckedHandler={isCheckedHandler}
            searchQueryHighlighter={searchQueryHighlighter}
            setCountryData={setDisplayCountrysData}
            setIsSelectedError={setIsSelectedError}
            showHeader={!searchTxt.length}
            setCountrySelectToastMsg={setCountrySelectToastMsg}
          />
        ) : (
          <ParaTag
            fontSize="sm"
            color={theme.greyText}
            margin="30px 0px 0px 0px"
          >
            No Country Found! Please enter correct name
          </ParaTag>
        )}
      </CountrySectionWrap>
      {isSelectedError && (
        <WebToast
          toastTxt={countrySelectErrMsg}
          top="475px"
          left="30px"
          height="40px"
          width="360px"
        />
      )}
      {isSelectedError && (
        <WebToast
          toastTxt={countrySelectErrMsg}
          top="475px"
          left="30px"
          height="40px"
          width="360px"
        />
      )}
      {countrySelectToastMsg && (
        <WebToast
          toastTxt={countrySelectToastMsg}
          top="475px"
          left="30px"
          height="40px"
          width="360px"
        />
      )}

      {displayCountryData?.length && (
        <BtnWrap>
          <LandingButton
            margin="16px 0px 0px 0px"
            customWidth="100%"
            customTitle={"Add Destination"}
            onClick={onCountrySubmit}
          />
        </BtnWrap>
      )}
    </>
  );

  if (isDropdown) {
    return (
      <DropdownContainer ref={dropdownRef}>
        <DropdownContent>
          <StickySearchContainer>
            <div onClick={searchClickAnalytics}>
              <InputWrapper>
                <InputIcon>
                  <img
                    src="https://tripmoneycmsimgak.mmtcdn.com/img/ic_search_5e79fda6b3.png"
                    width="9px"
                    height="9px"
                  />
                </InputIcon>
                <CountryInput
                  onChange={(e) => handleSearchTxt(e)}
                  placeholder="Search Country/Region"
                  autoFocus
                />
              </InputWrapper>
            </div>
          </StickySearchContainer>

          <ScrollableContent>
            <CountrySectionWrap>
              {displayCountryData?.length ? (
                <CountryList
                  customHeight={window.innerHeight * 0.3}
                  countryData={
                    searchTxt.length
                      ? displayCountryData.filter(
                          (country) => country.type !== "HEADER"
                        )
                      : displayCountryData
                  }
                  countryHandler={countryClickHandler}
                  isCheckedHandler={isCheckedHandler}
                  searchQueryHighlighter={searchQueryHighlighter}
                  setCountryData={setDisplayCountrysData}
                  setIsSelectedError={setIsSelectedError}
                  showHeader={!searchTxt.length}
                  setCountrySelectToastMsg={setCountrySelectToastMsg}
                />
              ) : (
                <ParaTag
                  fontSize="sm"
                  color={theme.greyText}
                  margin="30px 0px 0px 0px"
                >
                  No Country Found! Please enter correct name
                </ParaTag>
              )}
            </CountrySectionWrap>
          </ScrollableContent>

          {isSelectedError && (
            <WebToast
              toastTxt={countrySelectErrMsg}
              top="254px"
              left="12px"
              height="40px"
              width="360px"
            />
          )}
          {countrySelectToastMsg && (
            <WebToast
              toastTxt={countrySelectToastMsg}
              top="254px"
              left="12px"
              height="40px"
              width="360px"
            />
          )}

          {displayCountryData?.length && (
            <StickyButtonContainer>
              <LandingButton
                customWidth="30%"
                customHeight="38px"
                customTitle={"Apply"}
                onClick={onCountrySubmit}
              />
            </StickyButtonContainer>
          )}
        </DropdownContent>
      </DropdownContainer>
    );
  }

  return (
    <RegionCountryModal
      customHeight={"600px"}
      handleClick={onCountrySubmit}
      customTitle="Select Destinations"
      customSubTitle={customSubTitle}
    >
      {renderContent()}
    </RegionCountryModal>
  );
};

export default SelectCountryModalV2;
